/**
 * Members header — the member-count rail plus, for a holder of the matching
 * permission, "New role" and "Invite" CTAs.
 */
import { gravity } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html } from "lit";
import { when } from "lit/directives/when.js";
import { activeActor } from "../auth/actor.state.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import {
	detailState,
	inviteDraft,
	memberCount,
	pendingCount,
	roleDraft,
} from "./members.state.ts";

@schmancyElement("members-header")
export class MembersHeaderView extends LocalizeMixin(SchmancyElement) {
	override render() {
		const actor = activeActor.get();
		const orgId = actor?.orgId ?? "";
		const canManageRoles =
			actor?.permissions.includes("tickets.roles.manage") ?? false;
		const canInvite =
			actor?.permissions.includes("tickets.members.invite") ?? false;

		return html`
			<schmancy-surface
				${gravity({ distance: 18, mass: 1.1 })}
				type="container"
				class="block rounded-b-2xl px-3 py-3 md:px-6 md:py-5"
			>
				<div class="flex flex-wrap items-center justify-between gap-3">
					<div class="flex items-baseline gap-2 flex-wrap">
						<schmancy-typography type="display" token="" class="tabular-nums text-2xl md:text-4xl">
							${memberCount.get()}
						</schmancy-typography>
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${this.t(
								memberCount.get() === 1
									? "admin.members.list.memberSingular"
									: "admin.members.list.memberPlural",
							)}
						</schmancy-typography>
						${when(
							pendingCount.get() > 0,
							() => html`
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">·</schmancy-typography>
								<schmancy-typography type="display" token="" class="tabular-nums text-2xl md:text-4xl text-warning-default">
									${pendingCount.get()}
								</schmancy-typography>
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${this.t("admin.members.list.pendingLabel")}</schmancy-typography>
							`,
						)}
					</div>
					<div class="flex items-center gap-2 shrink-0">
						${when(
							canManageRoles,
							() => html`
								<schmancy-button
									variant="outlined"
									@click=${() => {
										roleDraft.replace({
											roleId: undefined,
											name: "",
											perms: new Set(),
											editing: true,
										});
										detailState.replace({ kind: "role" });
									}}
								>
									<schmancy-icon slot="prefix" aria-hidden="true">add</schmancy-icon>
									${this.t("admin.members.header.newRole")}
								</schmancy-button>
							`,
						)}
						${when(
							canInvite,
							() => html`
								<schmancy-button
									variant="filled"
									@click=${() => {
										inviteDraft.replace({
											email: "",
											displayName: "",
											// No pre-picked role — an admin default here would let a
											// send go out before anyone chose who gets what authority.
											roleId: "",
											scope: { kind: "all-events", orgId },
										});
										detailState.replace({ kind: "invite" });
									}}
								>
									<schmancy-icon slot="prefix" aria-hidden="true">person_add</schmancy-icon>
									${this.t("admin.members.header.invite")}
								</schmancy-button>
							`,
						)}
					</div>
				</div>
			</schmancy-surface>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"members-header": MembersHeaderView;
	}
}
